import { disposeAllManagedLabelsInSubtree } from "../labelUtils";
import { releaseAssetsInObject } from "../utils/assetUtils";
import { safeTraverse } from "./sceneObjectLookups";
import { logger } from "../../utils/logger";

export function disposeSceneObjectTree(root) {
  if (!root || typeof root.traverse !== "function") return;
  disposeAllManagedLabelsInSubtree(root);
  disposeRenderableResourcesInObject(root);
}

export function releaseSceneObjectTree(root) {
  if (!root) return;
  disposeAllManagedLabelsInSubtree(root);
  releaseAssetsInObject(root);
}

export function releaseAndDisposeSceneObjectTree(root) {
  if (!root) return;
  disposeAllManagedLabelsInSubtree(root);
  disposeRenderableResourcesInObject(root, { disposeSharedAssetGeometry: false });
  releaseAssetsInObject(root);
}

function disposeRenderableResourcesInObject(root, options = {}) {
  if (!root || typeof root.traverse !== "function") return;
  const { disposeSharedAssetGeometry = true } = options;
  const disposedGeometries = new WeakSet();
  const disposedMaterials = new WeakSet();
  const disposedTextures = new WeakSet();

  root.traverse((obj) => {
    const geometry = obj?.geometry;
    const assetBackedGeometry = obj?.userData?.assetId && !obj?.userData?.assetGeometryOwned;
    if (
      geometry &&
      typeof geometry.dispose === "function" &&
      (disposeSharedAssetGeometry || !assetBackedGeometry) &&
      !disposedGeometries.has(geometry)
    ) {
      geometry.dispose();
      disposedGeometries.add(geometry);
    }

    const material = obj?.material;
    const materials = Array.isArray(material) ? material : material ? [material] : [];
    materials.forEach((entry) => {
      if (!entry || disposedMaterials.has(entry)) return;
      disposeMaterialTextures(entry, disposedTextures);
      entry.dispose?.();
      disposedMaterials.add(entry);
    });
  });
}

function disposeMaterialTextures(material, disposedTextures) {
  Object.values(material || {}).forEach((value) => {
    if (!value?.isTexture || typeof value.dispose !== "function" || disposedTextures.has(value)) {
      return;
    }
    value.dispose();
    disposedTextures.add(value);
  });
}

// Remove any accidental cycles in the live Three.js graph.
// A cycle (adding an ancestor under its descendant) will break `updateMatrixWorld()` and rendering.
export function breakCyclesInObjectGraph(root) {
  if (!root) return 0;
  let removed = 0;

  // First pass: break parent-chain loops (node is in its own parent chain)
  safeTraverse(root, (node) => {
    try {
      const seenUp = new Set([node]);
      let p = node.parent;
      while (p) {
        if (seenUp.has(p)) {
          // Detach this node from its current parent to break the loop.
          if (node.parent && node.parent.remove) {
            logger.warn(
              "[Scene] Breaking parent-chain cycle by detaching node:",
              node?.name || "(unnamed)"
            );
            node.parent.remove(node);
            removed += 1;
          }
          break;
        }
        seenUp.add(p);
        p = p.parent;
      }
    } catch {
      // Best-effort cycle cleanup; keep traversing other nodes.
    }
  });

  // Second pass: for each edge parent->child, ensure child is not an ancestor of parent.
  safeTraverse(root, (parent) => {
    const children = Array.isArray(parent?.children) ? [...parent.children] : [];
    for (const child of children) {
      if (!child) continue;
      let p = parent;
      const seenUp = new Set();
      while (p) {
        if (seenUp.has(p)) break;
        seenUp.add(p);
        if (p === child) {
          logger.warn(
            "[Scene] Breaking scene graph cycle by removing child:",
            child?.name || "(unnamed)",
            "from parent:",
            parent?.name || "(unnamed)"
          );
          try {
            parent.remove(child);
            removed += 1;
          } catch {
            // Best-effort cycle cleanup; keep traversing other nodes.
          }
          break;
        }
        p = p.parent;
      }
    }
  });

  return removed;
}
